// `/` (UI-5): the morning's first look — what went out, what is running, what
// is red. The warmup curve of every pool together in the middle, the day's
// numbers above it, each pool in one line beside it, the running campaigns
// and the last day's alerts under it. Every number comes from the same reads
// as the warmup page.
//
// UI-8: the curve is linear, one stacked layer per pool in the pool's own
// colour under the dashed plan (the owner's decision of 2026-10-06, replacing
// the log scale). The layers are each started pool's own day curve — the same
// read the pool's page makes — so the stack adds up to the all-pools line.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BellIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  FlameIcon,
  InfoIcon,
  OctagonAlertIcon,
  PlusIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { fetchCampaignGroupOverview, fetchWarmupDays } from "@/lib/api";
import type { AlertRow, CampaignGroupRow, WarmupDays, WarmupPool } from "@/lib/api";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { Curve } from "@/components/emc/charts/Curve";
import { BAR_FILL } from "@/components/emc/charts/LimitBar";
import { PoolStack } from "@/components/emc/charts/PoolStack";
import type { StackDay } from "@/components/emc/charts/PoolStack";
import { Ring } from "@/components/emc/charts/Ring";
import { Sparkline } from "@/components/emc/charts/Sparkline";
import { poolColor } from "@/components/emc/charts/theme";
import { Country } from "@/components/emc/Flag";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { campaignGroupShown, statusLook } from "@/components/emc/status";
import type { Tone } from "@/components/emc/status";
import { TONE_TEXT } from "@/components/emc/tone";
import { cn } from "@/lib/utils";
import { messageOf } from "@/pages/campaigns/model";
import { useHaltStatus } from "@/pages/campaigns/data";
import { POLL_MS, useOverview } from "@/pages/warmup/data";
import { compact, curveData, dayLabel } from "@/pages/warmup/model";
import { shortAtViewer } from "@/components/emc/time";
import { alertCount, daySumLine, heldPools, poolLine, yesterdaySum } from "@/pages/overview/model";

// A campaign that is going out, or waiting on its A/B test to decide.
const RUNNING = ["sending", "ab_testing", "ab_deciding"] as const;
const GROUPS_SHOWN = 8;
// The overview curve's window (the API's OVERVIEW_DAYS): each pool's own
// curve is read over the same days, so its layer lines up with the total.
const CURVE_DAYS = 30;

function useRunningGroups() {
  const [rows, setRows] = useState<CampaignGroupRow[] | null>(null);
  const [scheduled, setScheduled] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const answer = await fetchCampaignGroupOverview({ states: [...RUNNING], limit: GROUPS_SHOWN });
      setRows(answer.items);
      setScheduled(answer.counts.scheduled ?? 0);
      setError(null);
    } catch (raised: unknown) {
      setError(messageOf(raised));
    }
  }, []);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [reload]);

  return { rows, scheduled, error };
}

/** Each started pool's own day curve, read with the overview's minute. A pool
 * whose read fails is left out of the stack, and the chart falls back to the
 * all-pools line rather than draw a stack with a layer missing. */
function usePoolCurves(codes: string[]) {
  const key = codes.join(",");
  const [curves, setCurves] = useState<{ key: string; byPool: Record<string, WarmupDays> } | null>(null);

  useEffect(() => {
    if (key === "") {
      setCurves({ key, byPool: {} });
      return undefined;
    }
    let cancelled = false;
    const read = async () => {
      try {
        const answers = await Promise.all(key.split(",").map((code) => fetchWarmupDays(code, CURVE_DAYS)));
        if (cancelled) return;
        const byPool: Record<string, WarmupDays> = {};
        answers.forEach((answer) => {
          byPool[answer.pool] = answer;
        });
        setCurves({ key, byPool });
      } catch {
        if (!cancelled) setCurves({ key, byPool: {} });
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [key]);

  return curves !== null && curves.key === key ? curves.byPool : null;
}

function poolTone(pool: WarmupPool): Tone {
  if (pool.is_paused || pool.watch.state === "paused") return "danger";
  if (pool.watch.state === "throttled" || pool.warmup_state === "holding") return "warn";
  return "running";
}

function PoolRow({ pool, index }: { pool: WarmupPool; index: number }) {
  const line = poolLine(pool);
  const sent = pool.today.rates?.sent ?? 0;
  const ratio = pool.budget_today > 0 ? sent / pool.budget_today : null;
  const stopped = line.tone === "danger";
  const cold = pool.warmup_state === "cold";
  return (
    <li className="relative [&+li]:before:absolute [&+li]:before:inset-x-5.5 [&+li]:before:top-0 [&+li]:before:h-px [&+li]:before:bg-separator">
      <Link
        to={`/warmup#pool-${pool.code}`}
        className="flex items-center gap-3 px-5.5 py-3 text-foreground transition-colors hover:bg-row-hover hover:no-underline"
      >
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-control bg-fill-secondary text-label font-title"
          style={{ color: poolColor(pool.code, index) }}
        >
          {pool.code}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-baseline justify-between gap-3">
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="truncate font-semibold">{pool.name || pool.code}</span>
              <span className="shrink-0 text-label text-muted-foreground">{pool.ip_count} IP</span>
            </span>
            {!stopped && !cold && (
              <span className="shrink-0 text-detail tabular-nums">
                <span className="font-semibold">{count(sent)}</span>
                <span className="text-muted-foreground"> / {count(pool.budget_today)}</span>
              </span>
            )}
            {stopped && <span className="shrink-0 text-detail font-semibold text-red-ink">áll</span>}
          </span>
          <span className="flex min-w-0 items-center gap-2.5">
            <StatusPill domain="warmup" state={pool.warmup_state} />
            {stopped || cold ? (
              <span className={cn("min-w-0 truncate text-label", TONE_TEXT[line.tone])} title={line.text}>
                {line.text}
              </span>
            ) : (
              <>
                <span
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={pool.budget_today}
                  aria-valuenow={sent}
                  aria-valuetext={line.text}
                  className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-pill bg-fill"
                >
                  <span
                    className={cn("block h-full rounded-pill transition-[width]", BAR_FILL[poolTone(pool)])}
                    style={{ width: `${Math.min(ratio ?? 0, 1) * 100}%` }}
                  />
                </span>
                <span className={cn("shrink-0 text-label tabular-nums", TONE_TEXT[line.tone])} title={line.text}>
                  {ratio === null ? "—" : `${Math.round(ratio * 100)}%`}
                  {line.tone === "warn" ? " · lassítva" : ""}
                </span>
              </>
            )}
          </span>
        </span>
        <ChevronRightIcon className="size-4 shrink-0 text-tertiary" />
      </Link>
    </li>
  );
}

function GroupRow({ group }: { group: CampaignGroupRow }) {
  const sent = group.countries.reduce((sum, country) => sum + country.sent_count, 0);
  const total = group.countries.reduce((sum, country) => sum + country.recipient_total, 0);
  return (
    <li className="flex flex-col gap-3 border-b border-separator py-4 first:pt-1 last:border-b-0">
      <div className="flex min-w-0 items-center gap-2">
        <Link to={`/kampanyok/${group.id}`} className="min-w-0 truncate text-body font-semibold text-foreground">
          {group.name}
        </Link>
        <StatusPill domain="campaignGroup" state={campaignGroupShown(group)} title={group.state} />
        <span className="ml-auto shrink-0 text-label text-muted-foreground tabular-nums">
          {count(sent)} / {count(total)}
        </span>
      </div>
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {group.countries.map((country) => {
          const look = statusLook("campaign", country.state);
          const ratio = country.recipient_total > 0 ? country.sent_count / country.recipient_total : 0;
          return (
            <div key={country.country} className="flex min-w-0 flex-col gap-1.5">
              <div className="flex min-w-0 items-center gap-2 text-detail">
                <Country code={country.country} />
                {country.state !== "sending" && <StatusPill domain="campaign" state={country.state} />}
                <span className="ml-auto shrink-0 text-label text-muted-foreground tabular-nums">
                  {count(country.sent_count)} / {count(country.recipient_total)}
                </span>
              </div>
              <span
                role="meter"
                aria-valuemin={0}
                aria-valuemax={country.recipient_total}
                aria-valuenow={country.sent_count}
                aria-valuetext={`${country.country}: ${count(country.sent_count)} / ${count(country.recipient_total)}, ${look.label}`}
                className="h-1.5 w-full overflow-hidden rounded-pill bg-fill"
              >
                <span
                  className={cn("block h-full rounded-pill transition-[width]", BAR_FILL[look.tone === "muted" ? "running" : look.tone])}
                  style={{ width: `${Math.min(ratio, 1) * 100}%` }}
                />
              </span>
            </div>
          );
        })}
      </div>
    </li>
  );
}

const ALERT_ICON: Record<string, { icon: typeof InfoIcon; tile: string }> = {
  critical: { icon: OctagonAlertIcon, tile: "bg-destructive text-destructive-foreground" },
  warning: { icon: TriangleAlertIcon, tile: "bg-orange-soft text-orange-ink" },
  info: { icon: InfoIcon, tile: "bg-gray-soft text-gray-ink" },
};

function AlertLine({ alert }: { alert: AlertRow }) {
  const seen = alert.acknowledged_at !== null;
  const look = ALERT_ICON[alert.level] ?? ALERT_ICON.info!;
  const Icon = look.icon;
  return (
    <li className="flex gap-3 border-b border-separator py-3.5 first:pt-1 last:border-b-0">
      <span
        aria-hidden="true"
        className={cn(
          "flex size-7.5 shrink-0 items-center justify-center rounded-control",
          seen ? "bg-fill text-muted-foreground" : look.tile,
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {alert.pool && <span className="font-semibold">{alert.pool}</span>}
          <span className="font-mono text-label text-muted-foreground">{alert.kind}</span>
          <StatusPill domain="alert" state={alert.level} />
          <span className="ml-auto text-label text-muted-foreground tabular-nums">
            {shortAtViewer(alert.occurred_at)}
          </span>
        </div>
        <p className={cn("m-0 text-detail break-words", seen && "text-muted-foreground")}>{alert.message}</p>
        {seen && <p className="m-0 text-label text-muted-foreground">nyugtázta: {alert.acknowledged_by}</p>}
      </div>
    </li>
  );
}

function todayInWords(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("hu-HU", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
}

export default function Overview() {
  const overview = useOverview();
  const running = useRunningGroups();
  const halt = useHaltStatus();
  const data = overview.data;
  const [shown, setShown] = useState("all");

  const started = useMemo(
    () => (data?.pools ?? []).filter((pool) => pool.warmup_state !== "cold").map((pool) => pool.code),
    [data],
  );
  const curves = usePoolCurves(started);

  if (data === null) {
    return (
      <>
        <PageHeader title="Áttekintés" />
        {overview.error !== null ? (
          <ErrorState message={overview.error} onRetry={() => void overview.reload()} />
        ) : (
          <LoadingState label="Az áttekintés betöltése…" />
        )}
      </>
    );
  }

  const { pools, totals, alerts } = data;
  const yesterday = yesterdaySum(pools);
  const held = heldPools(pools);
  const alerting = alertCount(alerts);
  const points = curveData(data.curve.points, data.today);

  // The stack: every started pool's whole days, on the all-pools window.
  const stackPools = curves === null ? [] : started.filter((code) => curves[code] !== undefined);
  const stackComplete = curves !== null && stackPools.length === started.length && started.length > 0;
  const stackDays: StackDay[] = data.curve.points.map((point) => {
    const layers: Record<string, number | null> = {};
    for (const code of stackPools) {
      const own = curves?.[code]?.curve.points.find((candidate) => candidate.day === point.day);
      layers[code] = point.day < data.today ? (own?.actual ?? null) : null;
    }
    return {
      label: dayLabel(point.day),
      plan: point.plan,
      total: point.day < data.today ? point.actual : null,
      pools: layers,
    };
  });
  const todayIndex = data.curve.points.findIndex((point) => point.day === data.today);
  const one = shown !== "all" ? curves?.[shown] : undefined;
  const lastDays = points
    .map((point) => point.actual)
    .filter((value): value is number => typeof value === "number")
    .slice(-8);
  const stoppedNames = new Set(held.stopped.map((pool) => pool.code));

  return (
    <>
      <PageHeader
        eyebrow={todayInWords(data.today)}
        title="Áttekintés"
        description="Mi ment ki, mi fut, mi piros — minden pool és kampány egy képernyőn. Percenként frissül."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/warmup">
                <FlameIcon />
                Warmup
              </Link>
            </Button>
            <Button asChild>
              <Link to="/kampanyok/uj">
                <PlusIcon />
                Új kampány
              </Link>
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-5">
        {halt.status?.halted && (
          <Callout tone="warn">
            A teljes kiküldés áll (a piros sáv fent): amíg a leállítás él, semmi nem megy ki.
          </Callout>
        )}
        {overview.error !== null && (
          <ErrorState message={overview.error} onRetry={() => void overview.reload()} />
        )}

        <StatGrid className="grid-cols-[repeat(auto-fill,minmax(15rem,1fr))]">
          <StatTile
            label="Ma kiment"
            value={count(totals.sent_today)}
            comparison={
              totals.budget_today > 0 ? `a mai keret: ${count(totals.budget_today)}` : "ma nincs keret"
            }
            aside={
              <Ring
                ratio={totals.budget_today > 0 ? totals.sent_today / totals.budget_today : null}
                tone={totals.sent_today > totals.budget_today ? "danger" : "running"}
                label="A mai keretből kiment"
              />
            }
          />
          <StatTile
            label="Tegnap kiment"
            value={yesterday.pools === 0 ? "—" : count(yesterday.sent)}
            comparison={daySumLine(yesterday)}
            aside={
              lastDays.length > 1 ? (
                <Sparkline
                  values={lastDays}
                  ariaLabel={`minden pool napi kiküldése, ${lastDays.length} nap`}
                  format={compact}
                  className="h-9 w-24"
                />
              ) : undefined
            }
          />
          <StatTile
            label="Álló pool"
            icon={<CircleAlertIcon />}
            value={
              <>
                {count(held.stopped.length)}
                <span className="text-section font-medium text-muted-foreground"> / {count(pools.length)}</span>
              </>
            }
            comparison={
              held.stopped.length > 0
                ? held.stopped.map((pool) => pool.code).join(", ")
                : held.slowed.length > 0
                  ? `egy sem áll; lassítva: ${held.slowed.map((pool) => pool.code).join(", ")}`
                  : "egy sem áll"
            }
            tone={held.stopped.length > 0 ? "danger" : held.slowed.length > 0 ? "warn" : "muted"}
          />
          <StatTile
            label="Riasztás, 24 óra"
            icon={<BellIcon />}
            value={
              <>
                {count(alerting.open)}
                <span className="text-section font-medium text-muted-foreground"> nyugtázatlan</span>
              </>
            }
            comparison={alerting.line}
            tone={alerting.tone}
          />
        </StatGrid>

        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
          <Card>
            <CardHeader>
              <CardTitle>Kiment levél naponta, minden pool</CardTitle>
              {started.length > 1 && (
                <CardAction>
                  <Tabs value={shown} onValueChange={setShown}>
                    <TabsList variant="segmented" aria-label="Melyik pool">
                      <TabsTrigger value="all">Összes</TabsTrigger>
                      {started.map((code) => (
                        <TabsTrigger key={code} value={code} disabled={curves?.[code] === undefined}>
                          {code}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </CardAction>
              )}
              <CardDescription>
                Színes: ami kiment, egész napokra, poolonként egymásra rakva; szaggatott: az elindított
                poolok rámpája tiszta napokkal.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {points.length === 0 ? (
                <EmptyState bare title="Még egy pool sem indult: nincs mit rajzolni." />
              ) : one !== undefined ? (
                <Curve
                  data={curveData(one.curve.points, data.today)}
                  formatValue={compact}
                  actualLabel={`${shown} kiment`}
                  planLabel="terv"
                  color={poolColor(shown, started.indexOf(shown))}
                  ariaLabel={`A(z) ${shown} pool napi kiküldése a tervhez képest`}
                  className="h-72"
                />
              ) : stackComplete ? (
                <PoolStack
                  days={stackDays}
                  pools={stackPools}
                  todayIndex={todayIndex >= 0 ? todayIndex : null}
                  formatValue={compact}
                  poolNote={(code) => (stoppedNames.has(code) ? "megállt" : null)}
                  ariaLabel="Minden pool napi kiküldése poolonként, a tervhez képest"
                />
              ) : (
                <Curve
                  data={points}
                  formatValue={compact}
                  actualLabel="kiment"
                  planLabel="terv"
                  ariaLabel="Minden pool napi kiküldése a tervhez képest"
                  className="h-72"
                />
              )}
            </CardContent>
          </Card>

          <Card className="gap-2 pb-2">
            <CardHeader>
              <CardTitle>Poolok</CardTitle>
              <CardAction>
                <Link to="/warmup" className="inline-flex items-center gap-0.5 text-detail font-medium hover:no-underline">
                  Warmup
                  <ChevronRightIcon className="size-4" />
                </Link>
              </CardAction>
              <CardDescription>Ma: kiment / keret. Egy sor egy pool; a sor a kártyájára visz.</CardDescription>
            </CardHeader>
            {pools.length === 0 ? (
              <CardContent>
                <p className="text-muted-foreground">Nincs egyetlen IP-pool sem felvéve.</p>
              </CardContent>
            ) : (
              <ul className="m-0 list-none p-0">
                {pools.map((pool, index) => (
                  <PoolRow key={pool.code} pool={pool} index={index} />
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="grid items-start gap-5 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Futó kampányok</CardTitle>
              <CardAction>
                <Link to="/kampanyok" className="inline-flex items-center gap-0.5 text-detail font-medium hover:no-underline">
                  Kampányok
                  <ChevronRightIcon className="size-4" />
                </Link>
              </CardAction>
              <CardDescription>
                Ami most megy ki, vagy az A/B döntésére vár; országonként kiment / címzett.
                {running.scheduled > 0 ? ` Ütemezve még ${count(running.scheduled)}.` : ""}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {running.error !== null && <ErrorState message={running.error} />}
              {running.rows === null && running.error === null && <LoadingState lines={2} />}
              {running.rows !== null && running.rows.length === 0 && (
                <p className="text-muted-foreground">Most nem fut kampány.</p>
              )}
              {running.rows !== null && running.rows.length > 0 && (
                <ul className="m-0 list-none p-0">
                  {running.rows.map((group) => (
                    <GroupRow key={group.id} group={group} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Riasztások — az utolsó 24 óra</CardTitle>
              <CardAction>
                <Link to="/kezbesites" className="inline-flex items-center gap-0.5 text-detail font-medium hover:no-underline">
                  Kézbesítés
                  <ChevronRightIcon className="size-4" />
                </Link>
              </CardAction>
              <CardDescription>A nyugtázatlanok elöl, azon belül a legújabb; az idők a te órád szerint.</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <p className="text-muted-foreground">Az utolsó 24 órában nem volt riasztás.</p>
              ) : (
                <ul className="m-0 list-none p-0">
                  {alerts.map((alert) => (
                    <AlertLine key={alert.id} alert={alert} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
